import { Tabs, Tab, Divider, Box } from "@mui/material"
import { FC, ReactElement, useMemo, useState } from "react"
import React from "react"
import { Link, matchPath, Redirect, RouteProps, useLocation } from "react-router-dom"
import { Content } from "@/components/content/Content"
import { Helmet } from 'react-helmet-async'
import withCompositeResource, { FallbackToRouteContext, WrappedComponentProps } from "@/components/recource/CompositeResource"
import { AlertAndTime } from "@/gen/api/encrypt"
import { ActionableAlertsContext } from "@/pages/obfuscation/EncryptionComposite"
import { allowEncryptSettingRoute, getSettingsMenuDefaultRoute } from "@/authProvider/permissionMap"
import { useHasFeature } from "@/utils/useHasFeature"
import { useOdPermissions } from "@/authProvider/useOdPermissions"


type TabOption = 'keys' | 'tokens' | 'obfuscation-rule' | 'info-types'
const tabSubRoutes = ['keys', 'tokens', 'obfuscation-rule', 'info-types']

const EncryptionSettings: FC<WrappedComponentProps> = ({
  children,
  tabs,
  options,
}) => {
  const permissions = useOdPermissions()
  const location = useLocation()
  const { rest: activeItem } = 
    matchPath<{rest:string}>(location.pathname, { path: '/settings/encryption/:rest'})?.params || {}

  const hasFeature = useHasFeature()

  const allowedTabs = useMemo(() => 
    tabs.filter(tab => 
      allowEncryptSettingRoute(tab.primaryPath || tab.path, hasFeature.OnDataDriver, hasFeature.OnDataSecureDB, permissions)), 
  [tabs, permissions, hasFeature.OnDataDriver, hasFeature.OnDataSecureDB])
  const switcherElem = children as ReactElement
  const [resElems, ...rest] = switcherElem.props.children as any[]
  const resElems2 = resElems.map((child:any) => {
    const res = child as ReactElement<RouteProps>
    const x = ((res.props.path !== '/settings/encryption/tokens') || hasFeature.OnDataDriver)
      ? res : null
    return x
  })
  
  const [alerts, setAlerts] = useState([] as AlertAndTime[])
  const actionableAlertsCtrl = useMemo(() => ({ alerts, setAlerts }), [alerts])
  
  const isTabSubRoute = !activeItem || tabSubRoutes.indexOf(activeItem) > -1
  if (isTabSubRoute && permissions && location.pathname.length > '/settings/encryption/'.length) {
    if (!allowEncryptSettingRoute(location.pathname, hasFeature.OnDataDriver, hasFeature.OnDataSecureDB, permissions)) {
      return <Redirect to="/" />
    }
  }
  return (
    <FallbackToRouteContext.Provider value={getSettingsMenuDefaultRoute({name:'Encryption', hasDriverFeature: hasFeature.OnDataDriver}, permissions) as any}>
      <ActionableAlertsContext.Provider value={actionableAlertsCtrl}>
        {isTabSubRoute
        ? <Content pageName="Encryption Settings" resourceName="Encryption Settings" breadcrumbs={options?.breadcrumbs} >
            <Helmet>
              <title>Encryption Settings | OnData</title>
            </Helmet>
            <Tabs
              indicatorColor="primary"
              scrollButtons="auto"
              textColor="primary"
              variant="scrollable"
              value={`/settings/encryption/${activeItem}`}
            >
              {allowedTabs.map((tab) => (
                <Tab
                  component={Link}
                  key={tab.path}
                  label={tab.label}
                  value={tab.path}
                  to={tab.primaryPath || tab.path}
                />
              ))}
            </Tabs>
            <Divider />
            <Box sx={{ mt: 3 }}>
              {React.cloneElement(switcherElem, { ...switcherElem.props, children: [resElems2, ...rest] })}
            </Box>
          </Content>
        : children  
        }
      </ActionableAlertsContext.Provider>
    </FallbackToRouteContext.Provider>
  )
}


export const EncryptionSettingsComposite = withCompositeResource(EncryptionSettings)

